import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DesignSettings, getCurrentDesign } from '@/services/settingsService'
import fallbackLogo from '@/assets/images/logo_new_en.svg'

const SPLASH_DURATION_MS = 1400
const BRAND_FALLBACK = 'Besalla'
const SPINNER_SIZE = 50

const resolveBrandText = (design?: DesignSettings | null) => {
  const candidate = design?.restaurantName?.trim() || design?.metaTitle?.trim()
  return candidate || BRAND_FALLBACK
}


const SplashScreen: React.FC = () => {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(true)
  const [brandText, setBrandText] = useState(() => resolveBrandText(getCurrentDesign()))
  const [headerLogoUrl, setHeaderLogoUrl] = useState<string | undefined>(() => getCurrentDesign().headerLogo)
  const timerRef = useRef<number | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    clearTimer()
    timerRef.current = window.setTimeout(() => {
      setVisible(false)
      timerRef.current = null
    }, SPLASH_DURATION_MS)
  }, [clearTimer])

  const triggerSplash = useCallback(() => {
    clearTimer()
    setVisible(true)
    scheduleHide()
  }, [clearTimer, scheduleHide])

  useEffect(() => {
    triggerSplash()
  }, [triggerSplash])

  useEffect(() => {
    const handleDesignSettings = (event: Event) => {
      const detail = (event as CustomEvent<DesignSettings | undefined>).detail
      setBrandText(resolveBrandText(detail))
      if (detail?.headerLogo) {
        setHeaderLogoUrl(detail.headerLogo)
      }
    }

    window.addEventListener('designSettingsApplied', handleDesignSettings as EventListener)
    return () => {
      window.removeEventListener('designSettingsApplied', handleDesignSettings as EventListener)
    }
  }, [])

  useEffect(() => {
    const handleHomeLogoClicked = () => {
      triggerSplash()
    }

    window.addEventListener('homeLogoClicked', handleHomeLogoClicked as EventListener)
    return () => {
      window.removeEventListener('homeLogoClicked', handleHomeLogoClicked as EventListener)
    }
  }, [triggerSplash])

  useEffect(() => () => clearTimer(), [clearTimer])

  const spinnerColor = 'var(--brand-primary, #0f172a)'


  const spinnerStyles = useMemo(
    () => ({
      width: SPINNER_SIZE,
      height: SPINNER_SIZE,
      border: '3px solid rgba(0, 0, 0, 0.1)',
      borderTopColor: spinnerColor,
    }),
    [spinnerColor],
  )

  if (!visible) {
    return null
  }

  const loadingLogoSrc =   fallbackLogo

  const spinnerRings = [0, 1, 2]

  return (
    <div
      aria-live="assertive"
      aria-label="Splash screen"
      className="fixed inset-0 z-[9999] flex min-h-screen flex-col items-center justify-center bg-white px-6 py-8 text-center"
    >
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-">
          <div className="relative flex h-[140px] w-[140px] items-center justify-center">
            {spinnerRings.map((ring) => (
              <span
                key={ring}
                className="absolute rounded-full border animate-spin"
                style={{
                  width: `${SPINNER_SIZE }px`,
                  height: `${SPINNER_SIZE}px`,
                  borderWidth: '1px',
                  borderColor: `${spinnerColor}`,
                  borderTopColor: 'transparent',
                  borderRightColor: 'transparent',
                }}
              />
            ))}
          </div>
          <div className="space-y-0 text-center">
            <p className="text-lg text-[#666666] max-w-[240px]">
              {t('loading.message')}
            </p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 flex items-center gap-2 -translate-x-1/2">
        <span className="text-sm text-[#666666] font-normal md:text-sm">
          {t('loading.poweredBy')}
        </span>
        <img
          src={loadingLogoSrc}
          alt="brand logo"
          className="w-[120px] h-[120px] md:w-[120px] md:h-[120px] object-contain"
        />
      </div>
    </div>
  )
}

export default SplashScreen

